import React, { useState, useEffect } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  Button,
  Chip,
  Stack,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { Issue } from '../types/issue';
import { useArticles } from '../context/ArticleContext';
import { useIssues } from '../context/IssueContext';
import { ArticleStatus } from '../services/articleService';

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
}));

const ArticleCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  transition: 'transform 0.2s',
  '&:hover': {
    transform: 'translateY(-4px)',
  },
}));

const IssueDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [issue, setIssue] = useState<Issue | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const { articles } = useArticles();
  const { getIssueById, incrementDownloads } = useIssues();

  useEffect(() => {
    const fetchIssue = async () => {
      try {
        setLoading(true);
        const data = await getIssueById(Number(id));
        setIssue(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Произошла ошибка');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchIssue();
    }
  }, [id, getIssueById]);

  const handleDownload = async () => {
    try {
      setIsDownloading(true);
      await incrementDownloads(Number(id));
      // Здесь можно добавить логику для скачивания файла
    } catch (err) {
      console.error('Ошибка при скачивании:', err);
    } finally {
      setIsDownloading(false);
    }
  };

  if (loading) {
    return (
      <Container sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error || !issue) {
    return (
      <Container sx={{ py: 4 }}>
        <Alert severity="error">{error || 'Выпуск не найден'}</Alert>
        <Button
          component={RouterLink}
          to="/issues"
          startIcon={<ArrowBackIcon />}
          sx={{ mt: 2 }}
        >
          Вернуться к списку выпусков
        </Button>
      </Container>
    );
  }

  return (
    <Container sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Button
          component={RouterLink}
          to="/issues"
          startIcon={<ArrowBackIcon />}
          sx={{ mb: 2 }}
        >
          Вернуться к списку выпусков
        </Button>
      </Box>

      <StyledPaper elevation={1}>
        <Grid container spacing={3}>
          {issue.coverImage && (
            <Grid item xs={12} md={4}>
              <Box
                component="img"
                src={issue.coverImage}
                alt={`Обложка выпуска ${issue.title}`}
                sx={{
                  width: '100%',
                  height: 'auto',
                  borderRadius: 1,
                }}
              />
            </Grid>
          )}
          <Grid item xs={12} md={issue.coverImage ? 8 : 12}>
            <Typography variant="h4" component="h1" gutterBottom>
              {issue.title}
            </Typography>           
          </Grid>
        </Grid>
      </StyledPaper>

      <Typography variant="h5" component="h2" gutterBottom>
        Статьи выпуска
      </Typography>

      {issue.articles && issue.articles.length > 0 ? (
        issue.articles
          .filter(article => article.status === ArticleStatus.PUBLISHED)
          .map((article) => (
          <ArticleCard key={article.id} elevation={1}>
            <Typography variant="h6" gutterBottom>
              {article.title}
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              {article.abstract}
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
              {Array.isArray(article.keywords) && article.keywords.map((keyword, index) => (
                <Chip key={index} label={keyword} size="small" />
              ))}
            </Stack>
            <Button
              component={RouterLink}
              to={`/articles/${article.id}/${article.title.toLowerCase().replace(/\s+/g, '-')}`}
              variant="outlined"
              size="small"
            >
              Читать статью
            </Button>
          </ArticleCard>
        ))
      ) : (
        <Typography variant="body1" color="text.secondary">
          В этом выпуске пока нет статей
        </Typography>
      )}
    </Container>
  );
};

export default IssueDetail; 